import api from './axios';

// [ANCHOR:FETCH_AGENT_TREE_NORMALIZE] 백엔드 응답 정규화: { ok, tree, message }
export async function fetchAgentTree() {
  const r = await api.get('/admin/users/tree');
  const data = r?.data ?? r;
  return {
    ok: Boolean(data?.ok ?? data?.tree),
    tree: data?.tree ?? null,
    message: data?.message ?? null,
  };
}

export async function createUser(payload) {
  // payload: { id, password, second_password?, nickname, as_agent, fee_percent? }  // fee_percent 단위: 퍼센트(0~100)
  const { data } = await api.post('/admin/users/create', payload);
  return data;
}

export async function blockUser(target_id, is_banned) {
  const { data } = await api.post('/admin/users/block', { target_id, is_banned });
  return data;
}

export async function deleteUserApi(target_id) {
  const { data } = await api.post('/admin/users/delete', { target_id });
  return data;
}

// ─────────────────────────────────────────────────────────────
// [ANCHOR:ASSET_API_NORMALIZE] — 자산 API 공통 정규화
function normalizeCreditDebit(resp) {
  // 백엔드 리치 페이로드 대응
  if (resp && resp.ok && (resp.src_user_code || resp.dst_user_code)) {
    return {
      ok: true,
      minted: Boolean(resp.minted),
      src: resp.src_user_code
        ? { user_code: String(resp.src_user_code), asset: (typeof resp.src_asset === 'number' ? resp.src_asset : null) }
        : null,
      dst: resp.dst_user_code
        ? { user_code: String(resp.dst_user_code), asset: (typeof resp.dst_asset === 'number' ? resp.dst_asset : null) }
        : null,
    };
  }
  // 레거시(단일 대상만 반환) 대응: { ok, user_code, asset }
  if (resp && (resp.user_code || typeof resp.asset !== 'undefined')) {
    return {
      ok: Boolean(resp.ok ?? true),
     minted: false,
      src: null,
      dst: {
        user_code: String(resp.user_code),
        asset: Number(resp.asset) || 0,
      },
    };
  }
  return { ok: false, minted: false, src: null, dst: null };
}

// ─────────────────────────────────────────────────────────────
// [ANCHOR:FEE_API] — 수수료(%) 수정 API
/** 대상 에이전트/유저에게 상위 Pool 안에서 배분할 상대 수수료 비율(0~100)을 수정 */
export async function updateAgentFee(agent_id, fee_percent) {
  const { data } = await api.post('/admin/agents/fee', { agent_id, fee_percent });
  return data; // { ok: true, agent_id, fee_percent }
}


// ─────────────────────────────────────────────────────────────
// [ANCHOR:ASSET_API] — 자산 조회/이체 API
/** 대상 유저(에이전트/유저) 자산 조회 */
export async function getAsset(target_id) {
  const { data } = await api.get('/admin/users/asset', { params: { target_id } });
  // data: { user_code, asset }
  return { ok: true, user_code: String(data.user_code), asset: Number(data.asset) || 0 };
}

/** 상위→하위 지급 (admin → child). 응답으로 양쪽 최신 잔액을 돌려줌 */
export async function creditAsset(target_id, amount) {
  const { data } = await api.post('/admin/users/asset/credit', { target_id, amount });
  return normalizeCreditDebit(data);
}

/** 하위→상위 회수 (child → admin). 응답으로 양쪽 최신 잔액을 돌려줌 */
export async function debitAsset(target_id, amount) {
  const { data } = await api.post('/admin/users/asset/debit', { target_id, amount });
  return normalizeCreditDebit(data);
}


// ─────────────────────────────────────────────────────────────
// [ANCHOR:LEDGER_API] — 자산기록 조회
/**
 * 자산 이동 기록 조회
 * params: {
 *   page?: number, pageSize?: number,
 *   q?: string,                 // id/user_code/nickname 검색
 *   asset_type?: 'asset'|'rakeback'|'all',
 *   from?: string, to?: string, // ISO(YYYY-MM-DD) 또는 YYYY-MM-DDTHH:mm
 *   sort?: 'desc'|'asc'
 * }
 * 백엔드 예상 응답:
 *   { ok:true, rows:[{...}], page, pageSize, total, hasMore }
 */
export async function fetchAssetLedger(params = {}) {
  const { data } = await api.get('/admin/assets/ledger', { params });
  // 가능한 레거시/임시 응답 호환
  return {
    ok: !!data?.ok ?? true,
    rows: Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []),
    page: Number(data?.page ?? params.page ?? 1),
    pageSize: Number(data?.pageSize ?? params.pageSize ?? 20),
    total: Number(data?.total ?? 0),
    hasMore: Boolean(data?.hasMore ?? false),
  };
}
// ─────────────────────────────────────────────────────────────
// [ANCHOR:MISSION_BENEFICIARY_API] — 테이블 미션 agent 추가 상금 수혜자 정책
export async function getMissionBeneficiaryPolicy(agent_id) {
  const { data } = await api.get(`/admin/mission/beneficiary/${encodeURIComponent(String(agent_id))}`);
  return data;
}

export async function updateMissionBeneficiaryPolicy(agent_id, policy) {
  const { data } = await api.put(`/admin/mission/beneficiary/${encodeURIComponent(String(agent_id))}`, { policy });
  return data;
}

// ─────────────────────────────────────────────────────────────
// [ANCHOR:ADMIN_PAYMENT_API] — 어드민 본인 충전/환전 API
export async function fetchAdminPaymentSummary() {
  const { data } = await api.get('/admin/payment/me');
  return data;
}

export async function requestAdminDepositAddress(payload = {}) {
  const { data } = await api.post('/admin/payment/deposit/address', payload);
  return data;
}

export async function fetchAdminDepositWalletWaitStatus(requestId) {
  const { data } = await api.get('/admin/payment/deposit/wait-status', { params: { request_id: requestId } });
  return data;
}

export async function createAdminDepositRequest(payload = {}) {
  const { data } = await api.post('/admin/payment/deposit/request', payload);
  return data;
}

export async function extendAdminDepositSession(payload = {}) {
  const { data } = await api.post('/admin/payment/deposit/extend', payload);
  return data;
}

export async function createAdminWithdrawRequest(payload = {}) {
  const { data } = await api.post('/admin/payment/withdraw/request', payload);
  return data;
}

export async function fetchAdminPaymentHistory(params = {}) {
  const { data } = await api.get('/admin/payment/history', { params });
  return data;
}

export async function cancelAdminPaymentRequest(request_id) {
  const { data } = await api.post('/admin/payment/cancel', { request_id });
  return data;
}

export async function recheckAdminPaymentRequest(request_id) {
  const { data } = await api.post('/admin/payment/recheck', { request_id });
  return data;
}


export async function fetchLivePaymentOperations(params = {}) {
  const { data } = await api.get('/admin/payment/live-operations', { params });
  return data;
}

export async function updateLivePaymentOperation(requestId, payload = {}) {
  const { data } = await api.post(`/admin/payment/live-operations/${encodeURIComponent(String(requestId))}/action`, payload);
  return data;
}

export async function fetchAdminPaymentLimits() {
  const { data } = await api.get('/admin/payment/limits');
  return data;
}

export async function fetchPaymentPolicySettings() {
  const { data } = await api.get('/admin/payment/policy');
  return data;
}

export async function updatePaymentPolicySettings(payload = {}) {
  const { data } = await api.put('/admin/payment/policy', payload);
  return data;
}

// ─────────────────────────────────────────────────────────────
// [ANCHOR:OPERATIONS_ANALYTICS_API] — 최상위 관리자 전용 운영 대시보드
export async function fetchOperationsDashboard(selection) {
  const params = typeof selection === 'string'
    ? { date: selection }
    : (selection?.mode === 'range'
      ? { start_date: selection.startDate, end_date: selection.endDate }
      : { date: selection?.date });
  const { data } = await api.get('/admin/analytics/summary', { params });
  return data;
}

export async function fetchOperationsHourly(selection) {
  const params = typeof selection === 'string'
    ? { date: selection }
    : (selection?.mode === 'range'
      ? { start_date: selection.startDate, end_date: selection.endDate }
      : { date: selection?.date });
  const { data } = await api.get('/admin/analytics/hourly', { params });
  return data;
}

export async function searchAnalyticsUsers(q = '', limit = 50) {
  const { data } = await api.get('/admin/analytics/excluded-users', { params: { q, limit } });
  return data;
}

export async function setAnalyticsUserExcluded(userCode, excluded) {
  const { data } = await api.put(`/admin/analytics/excluded-users/${encodeURIComponent(String(userCode))}`, { excluded: Boolean(excluded) });
  return data;
}

export async function refreshAnalyticsBalances() {
  const { data } = await api.post('/admin/analytics/refresh-balances');
  return data;
}
